// Página de formulario simple para que el operario complete sus datos
// Incluye selectores de escolaridad, ciudad y barrio, así como inputs de
// dirección, coordenadas y contacto. Las coordenadas pueden capturarse via
// Geolocation API con un botón dedicado.

import React, { useMemo, useState } from 'react'; // React y hooks de estado/memoización
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonList, IonItem, IonLabel, IonSelect, IonSelectOption, IonInput, IonButton, IonNote } from '@ionic/react'; // Componentes de Ionic
import { CIUDADES, BARRIOS_POR_CIUDAD } from 'src/data/ubicacionesPY'; // Datos locales de ciudades y barrios
import { getCurrentPosition, reverseGeocode } from 'src/utils/geo'; // Utilidad para geolocalización y reverse geocoding
import { PerfilOperario, getPerfil, savePerfil } from 'src/services/operarioLocal'; // Servicio de perfil local

// Declaramos la página como componente funcional por defecto
const ActualizarDatos: React.FC = () => {
  // Obtenemos el estado inicial desde localStorage para prellenar el formulario
  const inicial: PerfilOperario = useMemo(() => getPerfil(), []);
  // Creamos un estado controlado para todos los campos del formulario
  const [perfil, setPerfil] = useState<PerfilOperario>(inicial);
  // Calculamos los barrios disponibles cuando cambia la ciudad seleccionada
  const barriosDisponibles = useMemo(() => BARRIOS_POR_CIUDAD[perfil.ciudad || ''] || [], [perfil.ciudad]);

  // Handler genérico para actualizar cualquier campo del perfil por clave
  const onChange = (campo: keyof PerfilOperario, valor: string) => {
    setPerfil(prev => ({ ...prev, [campo]: valor }));
  };

  // Acción que intenta capturar las coordenadas actuales del dispositivo
  const capturarUbicacion = async () => {
    try {
      // Llamamos a la utilidad de geolocalización (solicita permiso al usuario)
      const pos = await getCurrentPosition();
      // Intentamos enriquecer con reverse geocoding para prellenar ciudad/barrio/dirección
      try {
        const rev = await reverseGeocode(pos.lat, pos.lng);
        setPerfil(prev => ({
          ...prev,
          latitud: pos.lat,
          longitud: pos.lng,
          ciudad: prev.ciudad || rev.ciudad || prev.ciudad,
          barrio: prev.barrio || rev.barrio || prev.barrio,
          direccion: prev.direccion || rev.direccion || prev.direccion,
        }));
      } catch {
        // Si falla el reverse geocoding, al menos guardamos las coordenadas
        setPerfil(prev => ({ ...prev, latitud: pos.lat, longitud: pos.lng }));
      }
    } catch (e: any) {
      // Si algo falla, mostramos un mensaje de alerta simple
      alert(e.message || 'No se pudo obtener la ubicación');
    }
  };

  // Acción de guardado: persiste el perfil en localStorage y avisa al usuario
  const guardar = () => {
    savePerfil(perfil);
    alert('Datos guardados correctamente');
  };

  // Render principal de la página
  return (
    <IonPage>
      {/* Encabezado con título */}
      <IonHeader>
        <IonToolbar color="primary">
          <IonTitle>Actualizar mis datos</IonTitle>
        </IonToolbar>
      </IonHeader>

      {/* Contenido scrollable con padding */}
      <IonContent className="ion-padding">
        <IonList inset>
          {/* Selector de escolaridad */}
          <IonItem>
            <IonLabel>Escolaridad</IonLabel>
            <IonSelect value={perfil.escolaridad} onIonChange={(e) => onChange('escolaridad', String(e.detail.value))}>
              <IonSelectOption value="Primaria Concluida">Primaria Concluida</IonSelectOption>
              <IonSelectOption value="Secundaria Concluida">Secundaria Concluida</IonSelectOption>
              <IonSelectOption value="Estudiante Terciario">Estudiante Terciario</IonSelectOption>
              <IonSelectOption value="Estudiante Universitario">Estudiante Universitario</IonSelectOption>
              <IonSelectOption value="Universidad Concluida">Universidad Concluida</IonSelectOption>
            </IonSelect>
          </IonItem>

          {/* Selector de ciudad */}
          <IonItem>
            <IonLabel>Ciudad</IonLabel>
            {/* Forzamos un modal con clase propia para listas largas y habilitar scroll (ver estilos en theme/main.css) */}
            <IonSelect value={perfil.ciudad} onIonChange={(e) => onChange('ciudad', String(e.detail.value))} interface="alert" interfaceOptions={{ cssClass: 'select-ciudades' }}>
              {CIUDADES.map((c) => (
                <IonSelectOption key={c} value={c}>{c}</IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>

          {/* Selector de barrio dependiente de ciudad */}
          <IonItem>
            <IonLabel>Barrio</IonLabel>
            {/* Misma estrategia de scroll para barrios dependientes de la ciudad seleccionada */}
            <IonSelect value={perfil.barrio} onIonChange={(e) => onChange('barrio', String(e.detail.value))} disabled={!perfil.ciudad} interface="alert" interfaceOptions={{ cssClass: 'select-barrios' }}>
              {barriosDisponibles.map((b) => (
                <IonSelectOption key={b} value={b}>{b}</IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>

          {/* Campo de dirección libre */}
          <IonItem>
            <IonLabel position="stacked">Dirección</IonLabel>
            <IonInput value={perfil.direccion || ''} onIonChange={(e) => onChange('direccion', String(e.detail.value))} placeholder="Calle y número, referencias" />
          </IonItem>

          {/* Inputs de coordenadas; editables manualmente por si el usuario prefiere */}
          <IonItem>
            <IonLabel position="stacked">Coordenadas de Latitud</IonLabel>
            <IonInput value={perfil.latitud || ''} onIonChange={(e) => onChange('latitud', String(e.detail.value))} placeholder="Ej: -25.2967" />
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">Coordenadas de Longitud</IonLabel>
            <IonInput value={perfil.longitud || ''} onIonChange={(e) => onChange('longitud', String(e.detail.value))} placeholder="Ej: -57.6459" />
          </IonItem>

          {/* Botón para intentar capturar ubicación actual del dispositivo */}
          <IonItem lines="none">
            <IonButton onClick={capturarUbicacion}>Obtener mi ubicación actual</IonButton>
            <IonNote slot="end">Requiere permiso del navegador</IonNote>
          </IonItem>

          {/* Datos de contacto de emergencia */}
          <IonItem>
            <IonLabel position="stacked">Nombre de Contacto</IonLabel>
            <IonInput value={perfil.contactoNombre || ''} onIonChange={(e) => onChange('contactoNombre', String(e.detail.value))} placeholder="Ej: Juan Pérez" />
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">Número del Contacto</IonLabel>
            <IonInput inputmode="tel" value={perfil.contactoTelefono || ''} onIonChange={(e) => onChange('contactoTelefono', String(e.detail.value))} placeholder="Ej: 0981 123 456" />
          </IonItem>

          {/* Botón de guardado */}
          <IonItem lines="none" className="ion-margin-top">
            <IonButton color="primary" onClick={guardar}>Guardar</IonButton>
          </IonItem>
        </IonList>
      </IonContent>
    </IonPage>
  );
};

// Exportamos por defecto para simplificar la importación en el enrutador
export default ActualizarDatos;
